<template>
  <sf-modal
    v-if="modalOpen"
    class="sf-border-box"
    size="s"
    data-walkthrough-id="account-entries-modal"
    :scrollable-content="true"
    @close="close"
  >
    <template #title>
      <div class="sf-level sf-level-center">
        <div class="sf-c-text-light">
          {{ I18n.t('account_entries_modal.total_value') }}{{ unitLabel }}:
        </div>
        <strong>{{ totalValue }}</strong>
        <div class="sf-label">{{ periodName }}</div>
      </div>
    </template>

    <template #body>
      <div v-if="isLoading" class="sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else>
        <div
          class="
            sf-bg-snow sf-layer-rounded sf-layer-border sf-pad-l-2 sf-below-2
          "
        >
          <div class="sf-level">
            <span class="fa fa-filter sf-c-text"></span
            ><input
              v-model="filterText"
              :placeholder="I18n.t('account_entries_modal.search_placeholder')"
              class="sf-form-input-transparent sf-pad-v-1 sf-full-width"
              type="text"
            />
            <div
              v-if="filterText"
              class="icon icon-remove sf-dim-el sf-pointer sf-pad-r-2"
              @click="clearFilterText"
            ></div>
          </div>
        </div>
        <table class="sf-table sf-table-border sf-table-middle">
          <thead v-if="unit">
            <tr>
              <th></th>
              <th class="sf-align-r" width="1">{{ unitHeader }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="entry in accountEntriesToShow"
              :key="entry.accountNumberWithName"
              class="sf-hoverable"
            >
              <td class="sf-relative">
                <div
                  v-if="showAccountOrigingalName(entry)"
                  class="sf-c-text-light"
                >
                  {{ entry.accountOriginalNumberWithOriginalName }}
                </div>
                <div class="sf-c-text-dark sf-fwdb sf-font-m">
                  {{ entry.accountNumberWithName }}
                  <a
                    :href="entry.newAdjustmentPath"
                    class="sf-btn sf-btn-small sf-show-in-hover place-top-right"
                    data-heap="new-adjustment-account-collection"
                    @click.prevent="handleNewAdjustmentClick(entry)"
                    >{{ I18n.t('adjustment.title_new') }}</a
                  >
                </div>
              </td>
              <td class="sf-align-r" width="1">
                <a
                  v-if="entry.url"
                  :href="entry.url"
                  class="sf-a sf-c-primary"
                  target="_blank"
                  >{{ entry.value }}</a
                ><span v-else>{{ entry.value }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </sf-modal>
</template>

<script>
import api from '../api/index'
import { doesAccountNameSatisfyQuery } from '../../utils/account-search-helpers'
import { isFeatureSupported } from '@app/adapters/silverfin'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import { getQueryParam } from '@app/utils/url-helpers'

export default {
  components: {
    SfModal,
  },

  data() {
    return {
      modalOpen: false,
      isLoading: false,
      periodName: '',
      filterText: '',
      totalValue: '',
      accountEntries: [],
      unit: '',
    }
  },

  computed: {
    accountEntriesToShow() {
      if (this.filterText) {
        return this.accountEntries.filter(this.accountEntryIncludesText)
      } else {
        return this.accountEntries
      }
    },
    unitLabel() {
      return this.unit ? ` (${this.unit})` : ''
    },
    unitHeader() {
      return this.unit ? ` ${this.unit}` : ''
    },
  },

  methods: {
    openModal({
      totalValue,
      periodName,
      ledgerId,
      ranges,
      includeZerosValue,
      addRoundingDifference,
      unit,
      precision,
      analyticalDimensionId,
      starred,
    }) {
      this.isLoading = true
      this.modalOpen = true
      this.periodName = periodName
      this.totalValue = totalValue
      this.filterText = ''
      this.unit = unit

      this.getAccountEntries({
        ledgerId,
        ranges: ranges,
        includeZerosValue,
        addRoundingDifference,
        unit,
        precision,
        analyticalDimensionId,
        starred,
      })
    },

    close() {
      this.modalOpen = false
    },

    handleNewAdjustmentClick(entry) {
      this.close()

      const url = entry.newAdjustmentPath
      const stringArray = url.split('?')

      if (isFeatureSupported('supports_adjustments_modal')) {
        const accountEntryId = getQueryParam('account_entry_id', stringArray[1])

        window.modalCitizen.openModalPortal('adjustments-modal', {
          accountEntryId: accountEntryId
            ? parseInt(accountEntryId, 10)
            : undefined,
          mode: 'new',
        })
      } else {
        window.Turbolinks.visit(url)
      }
    },

    getAccountEntries({
      ledgerId,
      ranges,
      includeZerosValue,
      addRoundingDifference,
      unit,
      precision,
      analyticalDimensionId,
      starred,
    }) {
      api
        .getAccountEntries({
          ranges,
          ledgerId,
          includeZerosValue,
          addRoundingDifference,
          unit,
          precision,
          analyticalDimensionId,
          starred,
        })
        .then(({ accountEntries }) => {
          this.accountEntries = accountEntries
          this.isLoading = false
        })
    },

    accountEntryIncludesText(accountEntry) {
      const inName = doesAccountNameSatisfyQuery(
        accountEntry.accountNumberWithName,
        this.filterText
      )
      const inOriginalName = doesAccountNameSatisfyQuery(
        accountEntry.accountOriginalNumberWithOriginalName,
        this.filterText
      )
      return inName || inOriginalName
    },

    showAccountOrigingalName(accountEntry) {
      return (
        accountEntry.accountNumberWithName !==
        accountEntry.accountOriginalNumberWithOriginalName
      )
    },

    clearFilterText() {
      this.filterText = ''
    },
  },
}
</script>

<style>
.sf-hoverable .sf-show-in-hover {
  display: none;
}

.sf-hoverable:hover .sf-show-in-hover {
  display: inherit;
}

.place-top-right {
  position: absolute;
  right: 10px;
  top: 5px;
}
</style>
